import {
  useLocalstorageRef,
  createLocalStorageRef
} from "@fresh8/vue-utils/src/useLocalStorageRef";
import { provide as vueProvide, inject as vueInject, Ref, computed } from "vue";

export interface CapturedEvent {
  viewID: string;
  betData: any;
  adTagData: any;
  time: string;
}

const capturesIdMap: Record<string, Ref<CapturedEvent>> = {};

function createDebugContext() {
  const enableAnalytics = useLocalstorageRef("enable-analytics", Boolean, true);
  const enableCapture = useLocalstorageRef("enable-capture", Boolean, false);
  const captureIds = useLocalstorageRef("capture-ids", Array, [] as string[]);

  const capturedEvents = computed(() => {
    // cheeky little effect here, but can't think of a way around it
    for (const id of captureIds.value) {
      if (!capturesIdMap[id])
        capturesIdMap[id] = createLocalStorageRef(
          `captured-id-${id}`,
          Object,
          {} as CapturedEvent
        ).item;
    }

    return captureIds.value
      .map(id => capturesIdMap[id])
      .filter(val => val !== undefined)
      .map(val => val.value);
  });

  function setEvent(id: string, event: CapturedEvent) {
    if (!captureIds.value.includes(id)) {
      capturesIdMap[id] = useLocalstorageRef(
        `captured-id-${id}`,
        Object,
        event
      );
      captureIds.value = [...captureIds.value, id];
    }
    capturesIdMap[id].value = event; // have to do this to trigger setting local storage after ref creation
  }

  function deleteEvent(id: string) {
    if (captureIds.value.includes(id)) {
      const newIds = [...captureIds.value];
      newIds.splice(newIds.indexOf(id), 1);
      captureIds.value = newIds;
      try {
        localStorage.removeItem(`fresh8-ads-captured-id-${id}`);
      } catch (e) {}
    }
  }

  return {
    enableAnalytics,
    enableCapture,
    capturedEvents,
    setEvent,
    deleteEvent
  };
}

export type DebugContext = ReturnType<typeof createDebugContext>;

const debugSymbol = Symbol("analyticsProvider");

export function inject() {
  return vueInject<DebugContext>(debugSymbol);
}

export function provide(ctx: DebugContext) {
  vueProvide(debugSymbol, ctx);
}

export function setupContext() {
  const context = createDebugContext();
  provide(context);

  return context;
}
